'use client';

import type { ReactElement, ReactNode } from 'react';
import { toast } from 'sonner';
import { FaExclamationTriangle, RiInformationLine } from './icons';
import { cancelAutoDismiss, nextToastId, scheduleAutoDismiss } from './toastAutoDismiss';

export const STATUS_TOAST_MS = 9000;
export const ERROR_TOAST_MS = 8000;
export const INFO_TOAST_MS = 5000;

export type ToastAccent = 'amber' | 'blue';

const ACCENT_TEXT: Record<ToastAccent, string> = {
  amber: 'text-price',
  blue: 'text-accent-blue',
};

interface ToastProps {
  leading?: ReactNode;
  overline?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  accent?: ToastAccent;
  onClick?: () => void;
  onDismiss?: () => void;
}

export const ToastTile = ({ children }: { children: ReactNode }) => (
  <div className="relative z-10 h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-white/5">{children}</div>
);

export const ToastAlert = () => (
  <span className="relative z-10 flex size-6 shrink-0 items-center justify-center text-danger">
    <FaExclamationTriangle size={17} />
  </span>
);

export const ToastInfo = () => (
  <span className="relative z-10 flex size-6 shrink-0 items-center justify-center text-accent-blue">
    <RiInformationLine size={20} />
  </span>
);

export const ToastPulse = () => (
  <span className="relative z-10 flex size-2.5 shrink-0">
    <span className="absolute inline-flex size-full animate-ping rounded-full bg-accent-blue opacity-60" />
    <span className="relative inline-flex size-2.5 rounded-full bg-accent-blue" />
  </span>
);

// WHY: браузер не рисует backdrop-filter, пока идёт transform влёта — полупрозрачная заливка оставляет карточку нечитаемой
// весь полёт
export function Toast({ leading, overline, title, description, accent = 'amber', onClick, onDismiss }: ToastProps) {
  const content = (
    <>
      {leading}

      <div className="relative z-10 min-w-0 flex-1">
        {overline && <div className={`text-[11px] font-semibold uppercase tracking-wide ${ACCENT_TEXT[accent]}`}>{overline}</div>}

        <div className="line-clamp-3 text-sm font-bold leading-snug text-white">{title}</div>
        {description && <div className="mt-0.5 flex items-center gap-2 text-[12px] leading-snug text-white/70">{description}</div>}
      </div>
    </>
  );

  const shine = (
    <span className="animate-shot-ach-shine pointer-events-none absolute inset-y-0 left-0 z-20 w-1/2 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
  );

  const base =
    'animate-shot-ach-toast-in toast-glass relative mx-auto flex w-[min(360px,calc(100vw-32px))] items-center gap-3 overflow-hidden rounded-2xl p-3 text-start backdrop-blur-[15px] backdrop-saturate-[180%] backdrop-brightness-125';

  const handleClick =
    onClick || onDismiss
      ? () => {
          onClick?.();
          onDismiss?.();
        }
      : undefined;

  if (!handleClick) {
    return (
      <div className={base}>
        {content}
        {shine}
      </div>
    );
  }
  return (
    <button type="button" onClick={handleClick} className={`${base} cursor-pointer transition active:opacity-90`}>
      {content}
      {shine}
    </button>
  );
}

// WHY: duration: Infinity — sonner тогда не ставит свой таймер, который в WebView замирает навсегда; время жизни ведёт
// toastAutoDismiss.ts
export const showCustomToast = (render: (id: string) => ReactElement, { duration, id = nextToastId() }: { duration: number; id?: string }) => {
  toast.custom(() => render(id), {
    id,
    duration: Infinity,
    onDismiss: () => cancelAutoDismiss(id),
  });
  scheduleAutoDismiss(id, duration, toast.dismiss);
  return id;
};

export const showStatusToast = (title: ReactNode, description?: ReactNode, id = 'payment-status') =>
  showCustomToast(
    (toastId) => (
      <Toast
        leading={<ToastPulse />}
        title={title}
        description={description}
        accent="blue"
        onDismiss={() => toast.dismiss(toastId)}
      />
    ),
    { id, duration: STATUS_TOAST_MS },
  );

export const showErrorToast = (title: ReactNode, description?: ReactNode, id?: string) =>
  showCustomToast(
    (toastId) => (
      <Toast
        leading={<ToastAlert />}
        title={title}
        description={description}
        onDismiss={() => toast.dismiss(toastId)}
      />
    ),
    { id, duration: ERROR_TOAST_MS },
  );

export const showInfoToast = (title: ReactNode, description?: ReactNode, id?: string) =>
  showCustomToast(
    (toastId) => (
      <Toast
        leading={<ToastInfo />}
        title={title}
        description={description}
        accent="blue"
        onDismiss={() => toast.dismiss(toastId)}
      />
    ),
    { id, duration: INFO_TOAST_MS },
  );
